import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  Grid,
  MenuItem,
  FormControl,
  Typography,
  Box,
  Slider,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Switch,
  Autocomplete,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import {
  CollapsedField,
  CustomInputLabel,
  CustomSelect,
  CustomTextField,
} from '../restyled-mui/CustomInputs'
import FormHelperText from '@mui/material/FormHelperText'
import DateRangePicker from './DateRangePicker/DateRangePicker'
import HelpIcon from '@mui/icons-material/Help'
import { PrimaryButton } from '../restyled-mui/CustomButton'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import { setSelectedUserSetup } from '../../redux/actions/userSetups'
import {
  setSelectedSetupForecast,
  setSelectedSetupPlant,
  setSelectedSetupForecastType,
  setIssueTime,
  setSelectedPercentile,
  setSelectedDateRange,
  setSelectedAggregationLevel,
  setSelectedAggregationValue,
  setLeadTime,
  setDetailAnalysis,
} from '../../redux/actions/metrics'
import ValueLabelComponent from './DateRangePicker/ValueLabelComponent'
import LinearProgress from '@mui/material/LinearProgress'
import Api from '../../api'
import { transformSelectedSetup } from '../../api/utils'
import { loadStateFromLocalStorage } from '../../redux/utilsLocalStorage'
import moment from 'moment'
import { ToggleButtonGroup, ToggleButton } from '@mui/material'
import { getPreviousPath } from '../../redux/reducers'
import { convertLocalIssueTimeToUtc } from '../../utils/time'

const marks = [
  { value: 0, label: '00:00' },
  { value: 1439, label: '23:59' },
]

const forecasts = [
  { name: 'per_plant', label: 'Per Plant' },
  { name: 'aggregated', label: 'Aggregated' },
]
const BASE_FORECAST_TYPES = ['ID', 'DAH', 'Composite']

// an aggregation value arrives as { [name]: {...} }, and the name is its value
const getAggregationValueKey = aggregationValue =>
  Object.keys(aggregationValue)[0]

const calculateDateRange = dateRangeString => {
  const daysBack = {
    d1: 1,
    d2: 2,
    d3: 3,
    d7: 7,
    d30: 30,
    d180: 180,
    d365: 365,
  }

  const oneDayBack = moment().subtract(1, 'day')

  if (!daysBack[dateRangeString]) {
    console.error('Invalid date range string:', dateRangeString)
    return null
  }

  return {
    startDate: oneDayBack
      .clone()
      .subtract(daysBack[dateRangeString], 'days')
      .toDate(),
    endDate: oneDayBack.toDate(),
  }
}

const sitesListWidth = 350
const tabletSidebarWidth = 50

const useStyles = makeStyles(theme => ({
  childrenContainer: {
    marginLeft: props =>
      props.fullscreen || props.toggleable ? 0 : sitesListWidth,
    width: props =>
      props.fullscreen || props.toggleable
        ? '100%'
        : `calc(100% - ${sitesListWidth}px)`,
    display: 'flex',
    flexDirection: 'column',
  },
  formContainer: {
    backgroundColor: theme.palette.common.white,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '15px 25px',
  },
  drawer: {
    marginLeft: props => (props.toggleable ? tabletSidebarWidth - 1 : 0),
    borderRight: `${theme.palette.menu.border} 1px solid`,
    width: props => (props.fullscreen ? '100%' : sitesListWidth),
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    width: tabletSidebarWidth,
    zIndex: 1400,
    backgroundColor: theme.palette.menu.bg,
    borderRight: `${theme.palette.menu.border} 1px solid`,
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 4),
  },
  menuItemIcon: {
    textAlign: 'center',
    minWidth: '100%',
  },
  menuIcon: {
    color: theme.palette.menu.icons,
  },
  selectedMenuIcon: {
    color: theme.palette.primary.main,
  },
  tickIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
  },
  customSubtitle: {
    fontSize: 14,
    color: theme.palette.primary.main,
    marginBottom: '10px',
  },
  timeSlider: {
    marginTop: '33px',
  },
  calendarField: {
    paddingRight: 0,
  },
  checkboxText: {
    color: theme.palette.primary.main,
    marginTop: '12px',
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  helpIcon: {
    color: theme.palette.primary.main,
  },
  customText: {
    fontSize: 14,
    color: theme.palette.primary.main,
    padding: '30px 0px',
  },
  startMetricButton: {
    width: '100%',
  },
  tooltipContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '4px',
  },
  sectionDelimiter: {
    paddingTop: '20px',
  },
  runButtonDelimiter: {
    paddingTop: '40px',
  },
  plantsDateErrorCss: {
    margin: '0px 5px',
    color: '#C63C3C',
  },
  toggleButtonPercent: {
    '&.Mui-selected': {
      backgroundColor: '#E6F1FC',
      color: '#368ED8',
      borderColor: '#A9CFF2',
    },

    '&.Mui-selected:hover': {
      backgroundColor: '#D6E9FB',
    },

    '&.Mui-selected.Mui-disabled': {
      backgroundColor: '#eee',
      color: '#888 !important',
      borderColor: '#ddd !important',
      opacity: 1,
      pointerEvents: 'none',
    },
  },
}))

const toggleButtonGroupStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',

  '& .MuiToggleButtonGroup-grouped': {
    margin: '4px !important',
    border: '1px solid rgba(0,0,0,0.12) !important',
  },

  '& .MuiToggleButton-root': {
    flex: '1 0 calc(100% / 7 - 8px)',
    maxWidth: 'calc(100% / 7 - 8px)',
  },
}

const MetricsForm = props => {
  const classes = useStyles(props)
  const {
    selectedSetup: rawSelectedSetup,
    setSelectedUserSetup,
    userSetups,
    userPowerSetups,
    setMetricsParameters,
    handleNoDataContentUpdate,
    setMetricsData,
    onChangeSelectedForecast,
    disableButton,
    selectedSetupForecast,
    setSelectedSetupForecast,
    selectedSetupPlant,
    setSelectedSetupPlant,
    selectedSetupForecastType,
    setSelectedSetupForecastType,
    issueTime,
    setIssueTime,
    selectedPercentile,
    setSelectedPercentile,
    selectedDateRange,
    setSelectedDateRange,
    selectedAggregationLevel,
    setSelectedAggregationLevel,
    selectedAggregationValue,
    setSelectedAggregationValue,
    leadTime,
    setLeadTime,
    detailAnalysis,
    setDetailAnalysis,
    trackDisableStatus,
  } = props

  // Validate selectedSetup against userPowerSetups
  const selectedSetup = userPowerSetups.includes(rawSelectedSetup)
    ? rawSelectedSetup
    : null

  const location = useLocation()

  const [timeDisableBtn, setTimeDisableBtn] = useState(false)

  // plants
  const [plants, setPlants] = useState(null)
  const [plantLoading, setPlantLoading] = useState(false)
  const [plantsError, setPlantsError] = useState('')

  // aggregation levels
  const [aggregationLevels, setAggregationLevels] = useState([])
  const [aggregationLevelsLoading, setAggregationLevelsLoading] =
    useState(false)

  /* What each fetched list belongs to. Until it matches the current selection
     the list is the previous one's, and must not be auto-selected. Values are
     fetched per level, so they are keyed by setup and level together. */
  const aggregationLevelsSetup = useRef(null)
  const plantsSetup = useRef(null)
  const aggregationValuesKey = useRef(null)

  // aggregation values
  const [aggregationValues, setAggregationValues] = useState([])
  const [aggregationValuesLoading, setAggregationValuesLoading] =
    useState(false)

  // percentiles
  const [percentiles, setPercentiles] = useState([])
  const [percentilesLoading, setPercentilesLoading] = useState(false)
  const [percentValid, setPercentValid] = useState(true)

  // const [formState, setFormState] = useState({
  //   percentError: '',
  // })

  const [storedDataReady, setStoredDataReady] = useState(false)
  const buttonRefPlant = useRef(null)
  const buttonRefAggr = useRef(null)
  const [isFullyRendered, setIsFullyRendered] = useState(false)
  const [domChangesDetected, setDomChangesDetected] = useState(false)
  const [fromQualityMonitoringQuery, setFromQualityMonitoringQuety] =
    useState(false)

  // delete this commented out after some time
  // const [forecastTypes, setForecastTypes] = useState(BASE_FORECAST_TYPES)
  const isIssueTime = ['ID', 'DAH'].includes(selectedSetupForecastType)
  const isComposite = selectedSetupForecastType === 'Composite'
  // const isLatestForecast = selectedSetupForecastType === 'Latest'

  useEffect(() => {
    const prevPath = getPreviousPath()
    const queryParams = new URLSearchParams(location.search)
    const fromHandler = queryParams.get('fromHandler') === 'true'
    setFromQualityMonitoringQuety(fromHandler)
    const storedData = loadStateFromLocalStorage('quality2MetricsData')

    if (fromHandler && storedData) {
      const { setupName, plantName, forecastType, dateRange } = storedData
      const newDateRange = calculateDateRange(dateRange)
      setSelectedDateRange(newDateRange)
      setSelectedUserSetup(setupName)
      setSelectedSetupPlant(plantName)
      setSelectedSetupForecastType(forecastType)
      setStoredDataReady(true)
    } else {
      console.log('Navigated directly, no handler data')
      /* 
      Resets the selected plant when coming from any route except /latest,
      so the plant selection is preserved when returning from the Latest page.
      */
      if (prevPath !== '/latest') {
        setSelectedSetupPlant(null)
      }
      /* 
      this is disabled currently because we want to 
      match latest module plant selection on both places
      metrics < == > latest
      */
      setStoredDataReady(true)
    }
  }, [location.search])

  //TODO - once the data come try out for both buttons
  useEffect(() => {
    if (buttonRefPlant.current || buttonRefAggr.current) {
      setIsFullyRendered(true)
    }
  })

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDomChangesDetected(true)
    })
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (
      fromQualityMonitoringQuery &&
      isFullyRendered &&
      storedDataReady &&
      domChangesDetected
    ) {
      startMetrics()
    }
  }, [
    isFullyRendered,
    storedDataReady,
    domChangesDetected,
    fromQualityMonitoringQuery,
  ])

  // const validateForm = () => {
  //   const errors = {
  //     percentError:
  //       selectedPercentile.length === 0
  //         ? 'At least one selected percentile  is required.'
  //         : '',
  //   }
  //   setFormState(prevForm => ({ ...prevForm, ...errors }))
  //   return Object.values(errors).every(error => !error)
  // }

  const timezone =
    loadStateFromLocalStorage('timezone') !== undefined
      ? loadStateFromLocalStorage('timezone')
      : 'Etc/UTC'

  const issueStartUtc = convertLocalIssueTimeToUtc(
    issueTime,
    selectedDateRange?.startDate ?? new Date(),
    timezone,
  )
  const issueEndUtc = convertLocalIssueTimeToUtc(
    issueTime,
    selectedDateRange?.endDate ?? selectedDateRange?.startDate ?? new Date(),
    timezone,
  )
  const issueTimeUTC = issueStartUtc.minutes

  const handleForecastChange = event => {
    const selectedForecastName = event.target.value
    const selectedForecastObject = forecasts.find(
      forecast => forecast.name === selectedForecastName,
    )
    setSelectedSetupForecast(selectedForecastObject)
    onChangeSelectedForecast(selectedForecastObject)
    setSelectedSetupPlant(null)
    setSelectedAggregationLevel(null)
    // the value belongs to the previous level, so it cannot survive it
    setSelectedAggregationValue('')
    setMetricsData(null)
    handleNoDataContentUpdate({ heading: '', body: '' })
  }

  const handleChangeLeadTime = e => {
    setMetricsData(null)
    const inputValue = parseInt(e.target.value, 10)

    if (!isNaN(inputValue)) {
      const nonNegativeValue = Math.max(0, inputValue)

      setLeadTime(nonNegativeValue)
    } else {
      setLeadTime(0)
    }
  }

  const handleSetupListData = (_, newValue) => {
    setSelectedSetupPlant(null)
    setSelectedAggregationLevel(null)
    // the value belongs to the previous setup's level, so it cannot survive it
    setSelectedAggregationValue('')
    setSelectedPercentile([50]) //add this to always on setup name change reset to 50
    setSelectedUserSetup(newValue)
    if (newValue !== null) {
      handleNoDataContentUpdate({ heading: '', body: '' })
    } else {
      handleNoDataContentUpdate({
        heading: 'No setup selected',
        body: 'Select a setup and adjust parameters in order to start analysis',
      })
    }
  }

  // create api call to get latest forecast type from setup - old way
  // useEffect(() => {
  //   const fetchSetup = async () => {
  //     const response = await Api.Setups.getSetup(selectedSetup)

  //     if (response.status === 200) {
  //       const latestForecast = response.data.config?.latest_forecast || false // add real value

  //       if (latestForecast) {
  //         // Add 'Latest' only if not already there
  //         setForecastTypes(prev =>
  //           prev.includes('Latest') ? prev : [...prev, 'Latest'],
  //         )
  //       } else {
  //         setForecastTypes(prev => prev.filter(type => type !== 'Latest'))
  //       }
  //     }
  //   }

  //   if (selectedSetup) fetchSetup()
  // }, [selectedSetup])

  // get setup aggregation levels
  useEffect(() => {
    const fetchAggregationLevel = async () => {
      setAggregationLevelsLoading(true)
      try {
        const response =
          await Api.Metrics.getClientAggregationLevels(selectedSetup)
        setAggregationLevels(response.data)
        aggregationLevelsSetup.current = selectedSetup
        setAggregationLevelsLoading(false)
      } catch (error) {
        setAggregationLevels([])
        aggregationLevelsSetup.current = selectedSetup
        console.error(
          'Error fetching aggregation levels for selected setup:',
          error,
        )
      } finally {
        setAggregationLevelsLoading(false)
      }
    }
    if (selectedSetup !== null) {
      fetchAggregationLevel()
    } else {
      setAggregationLevels([])
    }
  }, [selectedSetup])

  /* When there is only one option the select is collapsed to plain text, so
     nothing would ever select it: do it here. The list must belong to the
     selected setup, otherwise on a setup change we would select an option
     from the previous setup before its own list has arrived. */
  useEffect(() => {
    if (
      aggregationLevelsSetup.current === selectedSetup &&
      aggregationLevels.length === 1 &&
      !selectedAggregationLevel
    ) {
      setSelectedAggregationLevel(aggregationLevels[0])
    }
  }, [
    selectedSetup,
    aggregationLevels,
    selectedAggregationLevel,
    setSelectedAggregationLevel,
  ])

  useEffect(() => {
    if (
      plantsSetup.current === selectedSetup &&
      plants?.length === 1 &&
      !selectedSetupPlant
    ) {
      setSelectedSetupPlant(plants[0])
    }
  }, [selectedSetup, plants, selectedSetupPlant, setSelectedSetupPlant])

  useEffect(() => {
    const belongsToSelection =
      aggregationValuesKey.current ===
      `${selectedSetup}|${selectedAggregationLevel}`

    if (
      belongsToSelection &&
      aggregationValues.length === 1 &&
      selectedAggregationValue === ''
    ) {
      setSelectedAggregationValue(getAggregationValueKey(aggregationValues[0]))
    }
  }, [
    selectedSetup,
    selectedAggregationLevel,
    aggregationValues,
    selectedAggregationValue,
    setSelectedAggregationValue,
  ])

  // get setup aggregation values
  useEffect(() => {
    const fetchAggregationValues = async () => {
      setAggregationValuesLoading(true)
      try {
        const response = await Api.Metrics.getClientAggregationValues(
          selectedSetup,
          selectedAggregationLevel,
        )
        setAggregationValues(response.data)
        aggregationValuesKey.current = `${selectedSetup}|${selectedAggregationLevel}`
        setAggregationValuesLoading(false)
      } catch (error) {
        setAggregationValues([])
        aggregationValuesKey.current = `${selectedSetup}|${selectedAggregationLevel}`
        console.error(
          'Error fetching aggregation level for selected setup:',
          error,
        )
      } finally {
        setAggregationValuesLoading(false)
      }
    }
    if (selectedSetup !== null && selectedAggregationLevel !== null) {
      fetchAggregationValues()
    } else {
      setAggregationValues([])
    }
  }, [selectedSetup, selectedAggregationLevel])

  // get setup plants
  useEffect(() => {
    const fetchPlants = async () => {
      setPlantLoading(true)
      try {
        // const response = await axios.get('/metrics.json')
        const response = await Api.Metrics.getSetupPlants(
          selectedSetup,
          selectedDateRange,
        )

        const startDate = moment(selectedDateRange.startDate).format(
          'YYYY-MM-DD',
        )
        const endDate = moment(selectedDateRange.endDate).format('YYYY-MM-DD')

        // until get proper response
        if (response.status === 200) {
          plantsSetup.current = selectedSetup
          if (response.data.length !== 0) {
            setPlants(response.data)
            handleNoDataContentUpdate({ heading: '', body: '' })
            // setPlantsDateError('')
            setPlantsError('')
            // setDsbBtn(false)
            // setInitialPlantsLoad(false)
          } else {
            handleNoDataContentUpdate({
              heading: 'Info:',
              body: 'Please change the DATE SELECTION to get the active plants.',
            })
            setPlantsError(
              `No plants for selected period: ${startDate} to ${endDate}.`,
            )

            /* 
            this code was with this initial Plant Load as a 
            flag to get different errors and content for the 
            previous version where date selection was at the end of cycles
            */
            // if (initialPlantsLoad) {
            //   handleNoDataContentUpdate({ heading: '', body: '' })
            //   setInitialPlantsLoad(false)
            //   setPlantsError(
            //     `No plants for selected period: ${startDate} to ${endDate}.`,
            //   )
            //   // setPlantsDateError('')
            // } else {
            //   setDsbBtn(true)
            //   setPlantsError('')
            //   // setPlantsDateError('No plants for selected period.')
            //   handleNoDataContentUpdate({
            //     heading: 'Info:',
            //     body: 'Please change the DATE SELECTION to get the active plants.',
            //   })
            // }
            setPlants(null)
          }
        }
        setPlantLoading(false)
      } catch (error) {
        setPlants(null)
        setPlantsError('Error fetching plants for selected setup')
        console.error('Error fetching plants for selected setup:', error)
      } finally {
        setPlantLoading(false)
      }
    }
    if (selectedSetup !== null) {
      fetchPlants()
    } else {
      setPlants(null)
    }
  }, [selectedSetup, selectedDateRange])

  // get setup percentiles
  useEffect(() => {
    const fetchPercentiles = async () => {
      setPercentilesLoading(true)
      try {
        const response = await Api.Metrics.getSetupPercentiles(
          selectedSetup,
          selectedSetupForecast,
        )
        const percentiles = response.data
        const percentilesAsc = percentiles.sort((a, b) => a - b)
        setPercentiles(percentilesAsc)
        setPercentilesLoading(false)
      } catch (error) {
        setPercentiles([])
        console.error('Error fetching percentiles for selected setup:', error)
      } finally {
        setPercentilesLoading(false)
      }
    }
    if (selectedSetup !== null) {
      fetchPercentiles()
    } else {
      setPercentiles([])
    }
  }, [selectedSetup, selectedSetupForecast])

  const createMetricsObject = () => {
    const initObj = {
      client: transformSelectedSetup(selectedSetup),
      forecast: selectedSetupForecast.name,
      forecast_type: selectedSetupForecastType,
      percentiles: selectedPercentile,
      details: detailAnalysis,
    }

    const isPerPlant = selectedSetupForecast.name === 'per_plant'
    const isComposite = selectedSetupForecastType === 'Composite'
    const isLatest = selectedSetupForecastType === 'Latest'
    const usesUtcIssueDates = ['ID', 'DAH'].includes(selectedSetupForecastType)

    const fullInitObj = isLatest
      ? initObj
      : {
          ...initObj,
          start_date: usesUtcIssueDates
            ? issueStartUtc.date
            : moment(selectedDateRange.startDate).format('YYYY-MM-DD'),
          end_date: usesUtcIssueDates
            ? issueEndUtc.date
            : moment(selectedDateRange.endDate).format('YYYY-MM-DD'),
        }

    if (isPerPlant) {
      const base = {
        ...fullInitObj,
        plant: selectedSetupPlant,
      }

      if (isComposite) {
        // per_plant + Composite
        return {
          ...base,
          lead_time: leadTime,
          issue_time: 600, // hardcoded just for this case
        }
      }

      if (isLatest) {
        // per_plant + Latest
        return {
          ...base,
        }
      }

      // per_plant + ID/DAH
      return {
        ...base,
        issue_time: issueTimeUTC,
      }
    }

    // aggregated branch
    const aggregationEntry = aggregationValues.find(
      item => selectedAggregationValue in item,
    )

    const commonProps = {
      ...fullInitObj,
      aggregation_level: selectedAggregationLevel,
      aggregation_value: aggregationEntry
        ? aggregationEntry[selectedAggregationValue]
        : undefined,
    }

    if (isComposite) {
      // aggregated + Composite
      return {
        ...commonProps,
        lead_time: leadTime,
      }
    }

    if (isLatest) {
      // aggregated + Latest
      return {
        ...commonProps,
      }
    }

    // aggregated + ID/DAH
    return {
      ...commonProps,
      issue_time: issueTimeUTC,
    }
  }

  const startMetrics = () => {
    setMetricsParameters(createMetricsObject())
  }

  const isEmptyOrContainsUndefined =
    userSetups.length === 0 || userSetups.some(x => x === undefined)

  // Only one option to pick from, so there is nothing to pick: show as text.
  const isSingleSetup = userSetups.length === 1

  const renderSetupName = () => {
    if (isSingleSetup) {
      return (
        <Grid item md={12}>
          <CollapsedField label="Setup Name" value={userSetups[0]} />
        </Grid>
      )
    }

    return (
      <Grid item md={12}>
        <div className={classes.tooltipContainer}>
          <CustomInputLabel>Setup Name</CustomInputLabel>
          <Tooltip
            title="You can search for a value by typing in the box!"
            placement="top"
            arrow
            classes={{
              tooltip: classes.tooltip,
              arrow: classes.arrow,
            }}
          >
            <span>
              <IconButton
                style={{ padding: '0' }}
                aria-label="info"
                disableTouchRipple
              >
                <HelpIcon fontSize="small" className={classes.helpIcon} />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        <FormControl fullWidth variant="outlined" size="small">
          <Autocomplete
            disabled={trackDisableStatus}
            variant="outlined"
            size="small"
            options={userSetups}
            value={selectedSetup}
            autoHighlight
            onChange={handleSetupListData}
            renderInput={params => (
              <CustomTextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  style: { padding: '1px 5px' },
                }}
              />
            )}
          />
        </FormControl>
      </Grid>
    )
  }

  const renderForecast = () => {
    return (
      <Grid item md={12} className={classes.sectionDelimiter}>
        <Typography
          variant="h4"
          className={clsx(classes.formSubtitle, classes.customSubtitle)}
        >
          Forecast Type Selection
        </Typography>

        <CustomInputLabel>Forecast Type</CustomInputLabel>
        <FormControl
          disabled={trackDisableStatus}
          fullWidth
          margin="dense"
          variant="outlined"
          size="small"
        >
          <CustomSelect
            inputProps={{
              margin: 'dense',
            }}
            value={selectedSetupForecastType}
            onChange={e => {
              setSelectedSetupForecastType(e.target.value)
              setMetricsData(null)
            }}
            name={'forecastTypeId'}
          >
            {BASE_FORECAST_TYPES.map(ftype => {
              return (
                <MenuItem
                  key={ftype}
                  value={ftype}
                  className={classes.selectMenuItem}
                >
                  {ftype}
                </MenuItem>
              )
            })}
          </CustomSelect>
        </FormControl>

        <CustomInputLabel>Forecast</CustomInputLabel>
        <FormControl
          fullWidth
          margin="dense"
          variant="outlined"
          size="small"
          disabled={trackDisableStatus}
        >
          <CustomSelect
            inputProps={{
              margin: 'dense',
            }}
            value={selectedSetupForecast.name}
            onChange={handleForecastChange}
            name={'forecastId'}
          >
            {forecasts.map(forecast => {
              return (
                <MenuItem
                  key={forecast.name}
                  value={forecast.name}
                  className={classes.selectMenuItem}
                >
                  {forecast.label}
                </MenuItem>
              )
            })}
          </CustomSelect>
        </FormControl>
      </Grid>
    )
  }

  const renderDate = () => {
    return (
      <Grid item md={12} className={classes.sectionDelimiter}>
        <Typography
          variant="h4"
          className={clsx(classes.formSubtitle, classes.customSubtitle)}
        >
          Date selection
        </Typography>
        <Grid container spacing={2} style={{ paddingBottom: '15px' }}>
          <DateRangePicker
            trackDisableStatus={trackDisableStatus}
            initialDateRange={selectedDateRange}
            onDateRangeChange={dateRange => {
              setSelectedDateRange(dateRange)
              setMetricsData(null)
            }}
            onErrorChange={setTimeDisableBtn}
          />
        </Grid>
      </Grid>
    )
  }

  const renderPlants = () => {
    return (
      <div>
        {plantLoading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : plants !== null ? (
          plants.length === 1 ? (
            <CollapsedField label="Plant" value={plants[0]} />
          ) : (
            <>
              <CustomInputLabel>Plant</CustomInputLabel>
              <FormControl fullWidth variant="outlined" size="small">
                <Autocomplete
                  disabled={trackDisableStatus}
                  variant="outlined"
                  size="small"
                  options={plants}
                  value={selectedSetupPlant}
                  autoHighlight
                  onChange={(_, newPlant) => {
                    setSelectedSetupPlant(newPlant)
                    setMetricsData(null)
                  }}
                  renderInput={params => (
                    <CustomTextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        style: { padding: '1px 5px' },
                      }}
                    />
                  )}
                />
              </FormControl>
            </>
          )
        ) : (
          <CustomInputLabel style={{ color: 'orangered' }}>
            {plantsError}
          </CustomInputLabel>
        )}
      </div>
    )
  }

  const renderIssueTime = () => {
    return (
      <div>
        <CustomInputLabel>Issuetime ({timezone})</CustomInputLabel>
        <Box className={classes.timeSlider}>
          <Slider
            size="small"
            disabled={trackDisableStatus}
            aria-label="Always visible"
            value={issueTime}
            onChange={(_, newSliderValue) => {
              setIssueTime(newSliderValue)
              setMetricsData(null)
            }}
            defaultValue={600}
            step={5}
            marks={marks}
            valueLabelDisplay="on"
            min={0}
            max={1439}
            slots={{ valueLabel: ValueLabelComponent }}
          />
        </Box>
      </div>
    )
  }

  const renderLeadTime = () => {
    return (
      <div>
        <CustomInputLabel>Leadtime (minutes)</CustomInputLabel>
        <TextField
          disabled={trackDisableStatus}
          fullWidth
          margin="dense"
          variant="outlined"
          size="small"
          value={leadTime}
          onChange={handleChangeLeadTime}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="decrease"
                  onClick={() => {
                    const newValue = Math.max(leadTime - 15, 0)
                    setLeadTime(newValue)
                    setMetricsData(null)
                  }}
                  disabled={leadTime - 15 < 0 || trackDisableStatus}
                >
                  <RemoveIcon />
                </IconButton>
                <IconButton
                  disabled={trackDisableStatus}
                  size="small"
                  aria-label="increase"
                  onClick={() => {
                    const newValue = leadTime + 15
                    setLeadTime(newValue)
                    setMetricsData(null)
                  }}
                >
                  <AddIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
    )
  }

  const renderPercentiles = () => {
    return (
      <div>
        <CustomInputLabel style={{ marginBottom: '4px' }}>
          Percentiles
        </CustomInputLabel>
        {percentilesLoading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : percentiles.length !== 0 ? (
          <>
            <ToggleButtonGroup
              value={selectedPercentile}
              onChange={(event, newSelection) => {
                setSelectedPercentile(newSelection)
                setPercentValid(newSelection.length > 0)
                setMetricsData(null)
              }}
              size="small"
              color="primary"
              disabled={trackDisableStatus}
              fullWidth
              sx={toggleButtonGroupStyles}
            >
              {percentiles.map(percentile => (
                <ToggleButton
                  key={percentile}
                  value={percentile}
                  className={classes.toggleButtonPercent}
                  disabled={trackDisableStatus}
                >
                  {percentile}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {/* <FormHelperText id="component-error-text">
              {formState.percentError}
            </FormHelperText> */}

            {!percentValid && (
              <FormHelperText id="component-error-text">
                At least one selected percentile is required.
              </FormHelperText>
            )}
          </>
        ) : (
          <CustomInputLabel>
            No available percentiles for selected setup.
          </CustomInputLabel>
        )}
      </div>
    )
  }

  const renderRunBtn = buttonRef => {
    return (
      <div className={classes.runButtonDelimiter}>
        <PrimaryButton
          ref={buttonRef}
          onClick={startMetrics}
          style={{
            background:
              (disableButton || timeDisableBtn || !percentValid) && '#ccc',
          }}
          className={classes.startMetricButton}
          disabled={disableButton || timeDisableBtn || !percentValid}
        >
          RUN
        </PrimaryButton>
      </div>
    )
  }

  const renderAggregationLevel = () => {
    if (!aggregationLevelsLoading && aggregationLevels.length === 1) {
      return (
        <Grid item md={12}>
          <CollapsedField
            label="Aggregation Level"
            value={aggregationLevels[0]}
          />
        </Grid>
      )
    }

    return (
      <Grid item md={12}>
        <CustomInputLabel>Aggregation Level</CustomInputLabel>
        {aggregationLevelsLoading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : aggregationLevels.length !== 0 ? (
          <FormControl fullWidth variant="outlined" size="small">
            <Autocomplete
              disabled={trackDisableStatus}
              variant="outlined"
              size="small"
              options={aggregationLevels}
              value={selectedAggregationLevel}
              autoHighlight
              onChange={(_, newAggrLevel) => {
                setSelectedAggregationLevel(newAggrLevel)
                setSelectedAggregationValue('')
                setMetricsData(null)
              }}
              renderInput={params => (
                <CustomTextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    style: { padding: '1px 5px' },
                  }}
                />
              )}
            />
          </FormControl>
        ) : (
          <CustomInputLabel>
            No aggregation levels for selected setup or error occurred.
          </CustomInputLabel>
        )}
      </Grid>
    )
  }

  const renderAggregationValue = () => {
    if (!aggregationValuesLoading && aggregationValues.length === 1) {
      return (
        <Grid item md={12}>
          <CollapsedField
            label="Aggregation Value"
            value={getAggregationValueKey(aggregationValues[0])}
          />
        </Grid>
      )
    }

    return (
      <Grid item md={12}>
        <CustomInputLabel style={{ marginBottom: '4px' }}>
          Aggregation Value
        </CustomInputLabel>
        {aggregationValuesLoading ? (
          <LinearProgress style={{ marginTop: '20px' }} />
        ) : aggregationValues.length !== 0 ? (
          <FormControl
            disabled={trackDisableStatus}
            fullWidth
            variant="outlined"
            size="small"
          >
            <CustomSelect
              inputProps={{
                margin: 'dense',
              }}
              value={selectedAggregationValue}
              onChange={e => {
                setSelectedAggregationValue(e.target.value)
                setMetricsData(null)
              }}
              name={'aggrValue'}
              defaultValue=""
            >
              {aggregationValues.map(obj => {
                const key = getAggregationValueKey(obj)
                return (
                  <MenuItem
                    key={key}
                    value={key}
                    className={classes.selectMenuItem}
                  >
                    {key}
                  </MenuItem>
                )
              })}
            </CustomSelect>
          </FormControl>
        ) : (
          <CustomInputLabel>
            No aggregation values for selected setup or error occurred.
          </CustomInputLabel>
        )}
      </Grid>
    )
  }

  const renderSectionHeadline = caption => {
    return (
      <Typography
        variant="h4"
        className={clsx(classes.formSubtitle, classes.customSubtitle)}
      >
        {caption}
      </Typography>
    )
  }

  const renderDetailAnalysis = () => {
    return (
      <Box display="flex" alignItems="baseline" mt={3.5}>
        <Switch
          style={{ marginRight: 8 }}
          color="primary"
          size="small"
          checked={detailAnalysis}
          onChange={(_, checked) => {
            setDetailAnalysis(checked)
            setMetricsData(null)
          }}
          disabled={trackDisableStatus}
        />

        <CustomInputLabel>Detailed Analysis</CustomInputLabel>
      </Box>
    )
  }

  return (
    <>
      {storedDataReady && (
        <div className={classes.formContainer}>
          <Grid container spacing={0}>
            <Grid item md={12}>
              <Typography
                variant="h4"
                className={clsx(classes.formSubtitle, classes.customSubtitle)}
                style={{ marginBottom: 0 }}
              >
                Setup selection
              </Typography>
              {!isEmptyOrContainsUndefined ? (
                renderSetupName()
              ) : (
                <CustomInputLabel>
                  No available setup for selection or error occurred.
                </CustomInputLabel>
              )}

              {selectedSetup && (
                <>
                  {renderForecast()}

                  {renderDate()}

                  {selectedSetupForecast.name === 'per_plant' && (
                    <>
                      {renderSectionHeadline('Configuration')}

                      {renderPlants()}

                      {selectedSetupPlant && (
                        <>
                          {isIssueTime && renderIssueTime()}

                          {isComposite && renderLeadTime()}

                          {renderPercentiles()}

                          {renderDetailAnalysis()}

                          {!percentilesLoading &&
                            percentiles.length > 0 &&
                            renderRunBtn(buttonRefPlant)}
                        </>
                      )}
                    </>
                  )}

                  {selectedSetupForecast.name === 'aggregated' && (
                    <>
                      {renderSectionHeadline('Configuration')}

                      {renderAggregationLevel()}

                      {selectedAggregationLevel && (
                        <>
                          {renderAggregationValue()}

                          {selectedAggregationValue !== '' &&
                            aggregationValues.length > 0 && (
                              <>
                                {(selectedSetupForecastType === 'ID' ||
                                  selectedSetupForecastType === 'DAH') &&
                                  renderIssueTime()}

                                {selectedSetupForecastType === 'Composite' &&
                                  renderLeadTime()}

                                {renderPercentiles()}

                                {/* {renderDetailAnalysis()} */}

                                {!percentilesLoading &&
                                  percentiles.length > 0 &&
                                  renderRunBtn(buttonRefAggr)}
                              </>
                            )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </div>
      )}
    </>
  )
}

const mapStateToProps = state => {
  return {
    selectedSetup: state.userSetups.selectedSetup,
    userPowerSetups: state.userSetups.userPowerSetups,
    selectedSetupForecast: state.metrics.selectedForecast,
    selectedSetupPlant: state.metrics.selectedPlant,
    selectedSetupForecastType: state.metrics.selectedForecastType,
    issueTime: state.metrics.issueTime,
    selectedPercentile: state.metrics.selectedPercentile,
    selectedDateRange: state.metrics.selectedDateRange,
    selectedAggregationLevel: state.metrics.selectedAggregationLevel,
    selectedAggregationValue: state.metrics.selectedAggregationValue,
    leadTime: state.metrics.leadTime,
    detailAnalysis: state.metrics.detailAnalysis,
  }
}

const mapDispatchToProps = {
  setSelectedUserSetup,
  setSelectedSetupForecast,
  setSelectedSetupPlant,
  setSelectedSetupForecastType,
  setIssueTime,
  setSelectedPercentile,
  setSelectedDateRange,
  setSelectedAggregationLevel,
  setSelectedAggregationValue,
  setLeadTime,
  setDetailAnalysis,
}

export default connect(mapStateToProps, mapDispatchToProps)(MetricsForm)
